/** Candidate only: native Kaching adds; this bridge reads the result and renders it. */
import {validatePolicy, validateCart, ownsMain, owningDeal, hasLegacyMain, hasKachingMain, matchingDeal,
  selectedMainQuantity, acknowledgeNativeAdd, planReplacement, assertBundleCharges, assertKachingCheckout, planKachingCart, planAddon,
  standaloneAddonLines, checkoutURL, cartSummary, bothAddonTargets, treatmentPresentation, canReplaceRecordedDeal} from './glazed-kaching-core.js?v=4a40a32ff5a2cb7e';
import {installProductFormAjax,appendOfferAttribution,readRenderedOffer,installOfferAttributionInputs,resolveRenderedVariant} from './glazed-kaching-form-ajax.js?v=c3d693793d53cafd';

/** Immediate, accessible feedback; confirmed prices stay hidden until native add settles. */
export function createPendingCartPresentation({doc,drawer,onReload}){
  const body=drawer.querySelector('.bd'),footer=drawer.querySelector('.ft'),header=drawer.querySelector('.hd');
  if(!body||!footer||!header)return {pending(){},fail(){},clear(){},destroy(){}};
  const panel=doc.createElement('div');panel.id='gzCartPending';panel.hidden=true;
  panel.style.cssText='padding:24px;min-height:120px;box-sizing:border-box';
  const message=doc.createElement('p');message.setAttribute('role','status');message.setAttribute('aria-live','polite');message.setAttribute('aria-atomic','true');
  message.style.cssText='margin:0;font:inherit;line-height:1.5';
  const reload=doc.createElement('button');reload.type='button';reload.hidden=true;
  reload.textContent='Volver a cargar el carrito';
  reload.style.cssText='margin-top:16px;min-height:44px;padding:8px 14px;border:1px solid currentColor;border-radius:8px;background:transparent;color:inherit;font:inherit;font-weight:600;cursor:pointer';
  reload.addEventListener('click',onReload);panel.append(message,reload);header.after(panel);
  let saved=null;
  const hideContents=()=>{
    if(!saved)saved={body:body.hidden,footer:footer.hidden};
    body.hidden=true;footer.hidden=true;panel.hidden=false;
  };
  const clear=()=>{
    if(!saved)return;
    body.hidden=saved.body;footer.hidden=saved.footer;
    panel.hidden=true;reload.hidden=true;message.textContent='';delete panel.dataset.state;saved=null;
  };
  return {
    pending(){hideContents();reload.hidden=true;message.textContent='Agregando tu tratamiento…';panel.dataset.state='loading';},
    fail(text){if(!saved)return;hideContents();message.textContent=text||'No pudimos confirmar tu carrito. Vuelve a cargarlo.';reload.hidden=false;panel.dataset.state='error';},
    clear,
    destroy(){clear();reload.removeEventListener('click',onReload);panel.remove();}
  };
}

/** Capture ownership when the request starts, never when its response arrives. */
export function observeNativeAdds(env,{isAdd,getIntent,onStart,onResponse,onFailure,onSettled}){
  const originalFetch=env.fetch, nativeFetch=originalFetch.bind(env),cleanups=[];
  const ambiguousStatus=status=>!Number.isFinite(status)||status===0||status>=500||status===408;
  async function confirm(data,intent){
    if(data?.errors||data?.status>=400){onFailure(intent,new Error('No se pudo añadir el tratamiento.'),ambiguousStatus(data.status));return;}
    const items=Array.isArray(data?.items)?data.items:[data];
    if(!items.length)throw new Error('No se confirmó la respuesta del alta.');
    validateCart({items,total_price:0,discount_codes:[]});
    await onResponse(data,intent);
  }
  const observedFetch=function(resource,options){
    const intent=isAdd(resource)?getIntent():null;
    if(intent)onStart(intent);
    let result;
    try{result=nativeFetch(resource,options);}catch(error){
      if(intent){onFailure(intent,error,true);onSettled(intent);}throw error;
    }
    if(intent)void result.then(async response=>{
      if(!response.ok){onFailure(intent,new Error('No se pudo añadir el tratamiento.'),ambiguousStatus(response.status));return;}
      try{await confirm(await response.clone().json(),intent);}
      catch(error){onFailure(intent,error,true);}
    },error=>onFailure(intent,error,true)).finally(()=>onSettled(intent));
    return result;
  };
  env.fetch=observedFetch;cleanups.push(()=>{if(env.fetch===observedFetch)env.fetch=originalFetch;});
  const xhr=env.XMLHttpRequest?.prototype;
  if(xhr){const originalOpen=xhr.open,originalSend=xhr.send,tracked=new WeakMap();
    const observedOpen=function(method,resource,...args){tracked.set(this,isAdd(String(resource))&&String(method).toUpperCase()==='POST');return originalOpen.call(this,method,resource,...args);};
    const observedSend=function(...args){
      const intent=tracked.get(this)?getIntent():null;
      if(intent){
        onStart(intent);let settled=false;
        const finish=()=>{if(!settled){settled=true;onSettled(intent);}};
        this.addEventListener('load',()=>{void(async()=>{
          try{
            if(this.status<200||this.status>=300){onFailure(intent,new Error('No se pudo añadir el tratamiento.'),ambiguousStatus(this.status));return;}
            await confirm(this.responseType==='json'?this.response:JSON.parse(this.responseText),intent);
          }catch(error){onFailure(intent,error,true);}finally{finish();}
        })();},{once:true});
        for(const name of ['error','abort','timeout'])this.addEventListener(name,()=>{
          onFailure(intent,new Error('La conexión no confirmó el tratamiento.'),true);finish();
        },{once:true});
        try{return originalSend.apply(this,args);}catch(error){onFailure(intent,error,true);finish();throw error;}
      }
      return originalSend.apply(this,args);
    };
    xhr.open=observedOpen;xhr.send=observedSend;
    cleanups.push(()=>{if(xhr.open===observedOpen)xhr.open=originalOpen;if(xhr.send===observedSend)xhr.send=originalSend;});
  }
  return ()=>cleanups.reverse().forEach(fn=>fn());
}

/** A lost write response is not evidence that the server rejected the mutation. */
export async function requestShopifyCart(fetch,url,body){
  const writing=body!==undefined;
  const failure=uncertain=>Object.assign(new Error('No pudimos confirmar tu carrito. Reintenta.'),
    {uncertainCartWrite:writing&&uncertain});
  let response,data;
  try{
    response=await fetch(url,{method:writing?'POST':'GET',credentials:'same-origin',cache:'no-store',
      headers:{Accept:'application/json',...(writing?{'Content-Type':'application/json'}:{})},
      ...(writing?{body:JSON.stringify(body)}:{})});
  }catch{throw failure(true);}
  try{data=await response.json();}catch{throw failure(response.ok||response.status>=500||response.status===408);}
  if(!response.ok || data?.errors || data?.status>=400)
    throw failure(response.status>=500||response.status===408||data?.status>=500||data?.status===408);
  const addResponse=/\/cart\/add(?:\.js)?(?:[?#]|$)/.test(url);
  try{
    if(!data||typeof data!=='object'||Array.isArray(data))throw new Error('Invalid response');
    if(addResponse){
      const items=Array.isArray(data.items)?data.items:[data];
      if(!items.length)throw new Error('Empty add response');
      validateCart({items,total_price:0,discount_codes:[]});
    }else validateCart(data);
  }catch{throw failure(true);}
  return data;
}

/** All displayed addon amounts refer to the same quantity already in the cart. */
export function addonPriceSummary(lines,offerCents){
  const quantity=lines.reduce((sum,line)=>sum+line.quantity,0);
  const totalCents=quantity?lines.reduce((sum,line)=>sum+line.final_line_price,0):offerCents;
  const knownOriginal=quantity>0&&lines.every(line=>Number.isSafeInteger(line.original_line_price)&&line.original_line_price>=0);
  const originalCents=knownOriginal?lines.reduce((sum,line)=>sum+line.original_line_price,0):null;
  const unitCents=quantity>0&&totalCents%quantity===0?totalCents/quantity:null;
  const sameUnit=unitCents!==null&&lines.every(line=>line.final_line_price===unitCents*line.quantity);
  return {quantity,totalCents,originalCents,unitCents:sameUnit?unitCents:null};
}

/** Restore only the exact persisted bar inside the already assigned native arm. */
export function createCartSelectionRestorer({policy,form,env,onChange=()=>{}}){
  let epoch=0,touched=false,target=null,attempted=null,timer=null,failed=false,destroyed=false;
  const cancelTimer=()=>{if(timer!==null)env.clearTimeout(timer);timer=null;};
  const clear=()=>{target=null;attempted=null;cancelTimer();onChange();};
  const retry=()=>{
    if(destroyed||touched||!target)return;
    if(String(form.querySelector('input[name="id"]')?.value)!==String(policy.curvesVariant)){clear();return;}
    const block=form.querySelector('kaching-bundles-block[deal-block]');
    if(!block)return;
    let data;
    try{data=JSON.parse(block.getAttribute('deal-block'));}catch{return;}
    // Never change the experiment, arm, policy or native deal configuration.
    if(data.id!==target.attribution.testId||resolveRenderedVariant(data,policy)!==target.attribution.variantId){clear();return;}
    const native=data.dealBars?.find(bar=>bar.id===target.attribution.barId);
    if(!native||native.quantity!==target.quantity||native.discountType!=='specific'||
      Math.round(Number(native.discountValue)*100)!==target.priceCents){clear();return;}
    const radio=[...block.querySelectorAll('input[type="radio"]')].find(input=>input.value===target.attribution.barId);
    if(!radio)return;
    if(radio.checked){clear();return;}
    if(attempted===block)return;
    attempted=block;
    // Public custom-element attribute from Kaching's own rendered component.
    // Its setter updates native selection, quantities, gifts and variant events.
    block.setAttribute('selected-deal-bar-id',target.attribution.barId);
    env.queueMicrotask(retry);
  };
  return {
    start(){epoch++;touched=false;failed=false;clear();return epoch;},
    userSelection(){touched=true;failed=false;clear();},
    offer(cart,ticket){
      if(destroyed||ticket!==epoch||touched)return;
      let deal;
      try{
        if(!hasKachingMain(cart,policy)||hasLegacyMain(cart,policy))return;
        assertBundleCharges(cart,policy);
        deal=matchingDeal(cart.items.find(line=>ownsMain(line,policy)),policy);
      }catch{return;}
      if(!deal?.attribution)return;
      target=deal;attempted=null;failed=false;cancelTimer();
      timer=env.setTimeout(()=>{
        if(!target||touched||destroyed)return;
        failed=true;target=null;attempted=null;timer=null;onChange();
      },2500);
      retry();onChange();
    },
    retry,
    state(){return {busy:!!target,failed,touched,epoch};},
    destroy(){destroyed=true;cancelTimer();target=null;}
  };
}

/** Keep a repeated click on the chosen native CURVES offer selected. */
export function retainNativeOfferSelection(block,policy,formVariant){
  if(!policy?.enabled||String(formVariant)!==String(policy.curvesVariant))return false;
  try{
    const offerText=block.getAttribute('deal-block'),configText=block.getAttribute('config');
    if(!offerText||!configText||offerText.length>200000||configText.length>200000)return false;
    const offer=JSON.parse(offerText),config=JSON.parse(configText);
    if(!offer||typeof offer!=='object'||Array.isArray(offer)||
       !config||typeof config!=='object'||Array.isArray(config))return false;
    if(!policy.deals.some(deal=>deal.attribution?.testId===offer.id&&
      deal.attribution.variantId===resolveRenderedVariant(offer,policy)))return false;
    // Change only the native option; never overwrite the deal, arm or selected bar.
    if(config.ignoreDeselect!==false)return false;
    block.setAttribute('config',JSON.stringify({...config,ignoreDeselect:true}));
    return true;
  }catch{return false;}
}

/** Retry only the initial read; never repeat an add or update. */
export async function readInitialCart(read,delay){
  for(let attempt=0;attempt<3;attempt++){
    try{return await read();}
    catch(error){if(attempt===2)throw error;await delay(attempt===0?250:750);}
  }
}

/** Discount on the identified CURVES pack only; gifts/add-ons never inflate it. */
export function packSavingsSummary(summary,policy){
  const mains=summary?.mains;
  if(!Array.isArray(mains)||!mains.length||!Array.isArray(policy?.deals))return null;
  const deals=mains.map(line=>matchingDeal(line,policy)),deal=deals[0];
  if(!deal||deals.some(item=>item!==deal))return null;
  let originalCents=0,finalCents=0,quantity=0;
  for(const line of mains){
    if(!Number.isSafeInteger(line.quantity)||line.quantity<=0||
       !Number.isSafeInteger(line.original_line_price)||line.original_line_price<=0||
       !Number.isSafeInteger(line.final_line_price)||line.final_line_price<0||
       line.final_line_price>line.original_line_price)return null;
    originalCents+=line.original_line_price;finalCents+=line.final_line_price;quantity+=line.quantity;
  }
  if(quantity!==deal.quantity||!Number.isSafeInteger(originalCents)||!Number.isSafeInteger(finalCents))return null;
  if(finalCents<=0||!Number.isSafeInteger(deal.priceCents)||Math.abs(finalCents-deal.priceCents)>1)return null;
  const savingsCents=originalCents-finalCents;
  if(savingsCents<=0)return null;
  const percent=Number(BigInt(savingsCents)*100n/BigInt(originalCents));
  return percent>0?{originalCents,finalCents,savingsCents,percent}:null;
}
/** Presentation-only fallback for the new MX two-bottle pack. */
export function packPresentationImage(summary,policy){
  const main=summary?.mains?.[0],deal=main?matchingDeal(main,policy):null;
  const treatmentTwo=policy?.market==='MX'&&deal?.experiment==='gz-treatment-mx-20261022'&&deal.quantity===2&&summary.quantity===2?
    'https://cdn.shopify.com/s/files/1/0884/4319/1653/files/Kaching-Bundles-pack-2_300x300.png?v=1792407939':null;
  return deal?.image||policy?.packImages?.[String(summary?.quantity)]||treatmentTwo||main?.image||null;
}
export function renderPackSavings(doc,savings){
  const box=doc.getElementById('gzPackSavings');
  if(!box)return;
  const percent=doc.getElementById('gzPackSavingsPercent'),fill=doc.getElementById('gzPackSavingsFill');
  box.hidden=!savings;
  if(percent)percent.textContent=savings?savings.percent+'% MENOS':'';
  if(fill)fill.style.width=savings?savings.percent+'%':'0%';
}

const removalCanonical=value=>value&&typeof value==='object'?
  Array.isArray(value)?value.map(removalCanonical):Object.fromEntries(Object.keys(value).sort().map(key=>[key,removalCanonical(value[key])])):value??null;
const removalIdentity=line=>JSON.stringify(removalCanonical([String(line.variant_id??line.id),line.quantity,
  line.properties||{},line.selling_plan_allocation?.selling_plan?.id??line.selling_plan??null,line.parent_relationship||null]));
const removalError=()=>new Error('El carrito cambió. Revisa el tratamiento y vuelve a pulsar Quitar.');
/** Capture only the CURVES rows the customer explicitly chose to remove. */
export function captureTreatmentRemoval(cart,policy,keys){
  validateCart(cart);
  if(!Array.isArray(keys)||!keys.length||new Set(keys).size!==keys.length)throw removalError();
  return {lines:keys.map(key=>{
    const line=cart.items.find(item=>item.key===key);
    if(!line||String(line.variant_id??line.id)!==String(policy.curvesVariant))throw removalError();
    return {key,identity:removalIdentity(line)};
  })};
}
/** One keyed update, with Shopify removing known children through their parent. */
export function planTreatmentRemoval(cart,policy,intent){
  validatePolicy(policy);validateCart(cart);
  if(!Array.isArray(intent?.lines)||!intent.lines.length||new Set(intent.lines.map(line=>line.key)).size!==intent.lines.length)throw removalError();
  const selected=intent.lines.map(expected=>{
    const line=cart.items.find(item=>item.key===expected.key);
    if(!line||String(line.variant_id??line.id)!==String(policy.curvesVariant)||removalIdentity(line)!==expected.identity)throw removalError();
    if(line.instructions?.can_remove===false)throw new Error('Este tratamiento está vinculado a otro producto y no se puede quitar por separado.');
    return line;
  });
  const updates=Object.fromEntries(selected.map(line=>[line.key,0]));
  const removed=new Set(selected.map(line=>line.key));
  const selectedDeals=new Set(selected.filter(line=>ownsMain(line,policy)).map(line=>matchingDeal(line,policy).id));
  const remaining=cart.items.filter(line=>!removed.has(line.key));
  for(const line of remaining){
    const parent=line.parent_relationship?.parent_key;
    const owner=owningDeal(line,cart,policy);
    const knownGift=owner&&owner.giftVariantIds.map(String).includes(String(line.variant_id??line.id));
    if(parent&&removed.has(parent)){
      if(!knownGift)throw new Error('El tratamiento contiene otro producto vinculado. No se quitó ningún artículo.');
      removed.add(line.key); // Shopify owns cascade removal; never write a locked child.
    }else if(!parent&&knownGift&&selectedDeals.has(owner.id)&&
      !remaining.some(item=>ownsMain(item,policy)&&matchingDeal(item,policy).id===owner.id)){
      if(line.instructions?.can_remove===false)throw new Error('No se puede quitar el regalo por separado. No se quitó el tratamiento.');
      updates[line.key]=0;removed.add(line.key);
    }
  }
  if(!remaining.some(line=>String(line.variant_id??line.id)===String(policy.curvesVariant))){
    for(const line of remaining){
      const plainLegacy=(policy.legacyGiftVariantIds||[]).map(String).includes(String(line.variant_id??line.id))&&
        line.final_line_price===0&&!line.parent_relationship?.parent_key&&!line.selling_plan_allocation&&!line.selling_plan&&
        Object.keys(line.properties||{}).length===0;
      if(!plainLegacy)continue;
      if(line.instructions?.can_remove===false)throw new Error('No se puede quitar el regalo anterior. No se quitó el tratamiento.');
      updates[line.key]=0;removed.add(line.key);
    }
  }
  return {updates,removedKeys:[...removed]};
}
/** A lost or partial removal is never permission to repeat the POST. */
export function verifyTreatmentRemoval(before,after,plan){
  validateCart(before);validateCart(after);
  if(after.items.some(line=>plan.removedKeys.includes(line.key)))throw new Error('No se confirmó que se quitó el tratamiento completo. Vuelve a cargar el carrito.');
  const retained=before.items.filter(line=>!plan.removedKeys.includes(line.key)).map(removalIdentity).sort();
  const actual=after.items.map(removalIdentity).sort();
  const codes=cart=>cart.discount_codes.map(item=>String(item.code).toLowerCase()).sort();
  if(JSON.stringify(retained)!==JSON.stringify(actual)||JSON.stringify(codes(before))!==JSON.stringify(codes(after)))
    throw new Error('El carrito cambió mientras quitábamos el tratamiento. Vuelve a cargarlo antes de continuar.');
  return after;
}

/** Expose only the recognized rendered arm, never infer assignment from cart history. */
export function publishTreatmentContext(doc,env,deal,policy){
  const section=doc.querySelector('.cv-landing');
  if(!section)return;
  const presentation=treatmentPresentation(deal);
  const detail={arm:deal.arm,experiment:deal.experiment||policy.experiment,
    dailyCapsules:deal.dailyCapsules||2,dealId:deal.id,quantity:deal.quantity,
    months:presentation.months,title:presentation.title};
  const signature=JSON.stringify(detail);
  if(section.dataset.gzTreatmentContext===signature)return;
  section.dataset.gzKachingArm=detail.arm;
  section.dataset.gzKachingExperiment=detail.experiment;
  section.dataset.gzKachingDailyCapsules=String(detail.dailyCapsules);
  section.dataset.gzTreatmentContext=signature;
  if(typeof env.CustomEvent==='function')doc.dispatchEvent(new env.CustomEvent('glazed:kaching-arm-ready',{detail}));
}

/** Reopening is read-only only when this exact native selection is already acknowledged. */
export function canReopenTreatment(cart,policy,selection){
  try{
    validatePolicy(policy);validateCart(cart);
    if(String(selection?.mainVariant)!==String(policy.curvesVariant)||hasLegacyMain(cart,policy))return false;
    const deal=policy.deals.find(item=>item.attribution?.testId===selection.testId&&
      item.attribution.variantId===selection.variantId&&item.attribution.barId===selection.barId);
    if(!deal||selection.quantity!==deal.quantity||selection.priceCents!==deal.priceCents)return false;
    const mains=cart.items.filter(line=>ownsMain(line,policy));
    if(!mains.length||mains.some(line=>matchingDeal(line,policy)!==deal))return false;
    assertBundleCharges(cart,policy);
    const required={gz_cart_engine:'kaching-v1',gz_offer_test:deal.experiment||policy.experiment,
      gz_offer_arm:deal.arm,gz_offer_deals:deal.id,gz_offer_revision:deal.revision||policy.revision};
    if(Object.entries(required).some(([key,value])=>!value||cart.attributes?.[key]!==value))return false;
    const plan=planKachingCart(cart,policy);
    return !!plan&&!Object.keys(plan.updates).length&&
      cart.discount_codes.map(item=>item.code).join(',')===plan.discountCodes.join(',');
  }catch{return false;}
}

export function canSelectOfferBeforeAdd(cart,selectedDeal,policy){
  return !!selectedDeal&&cart.items.filter(line=>ownsMain(line,policy))
    .every(line=>canReplaceRecordedDeal(matchingDeal(line,policy),selectedDeal,policy));
}

/** Readiness is the actual native three-card offer, never the host's initialized marker alone. */
export function nativeOfferReady(form,policy){
  try{
    if(!policy?.enabled||String(form.querySelector('input[name="id"]')?.value)!==String(policy.curvesVariant))return false;
    const block=form.querySelector('kaching-bundles-block[deal-block]');
    if(!block)return false;
    const raw=block.getAttribute('deal-block');if(!raw||raw.length>200000)return false;
    const data=JSON.parse(raw),variantId=resolveRenderedVariant(data,policy);
    if(!variantId)return false;
    const expected=policy.deals.filter(deal=>(deal.experiment||policy.experiment)===policy.experiment&&
      deal.attribution?.testId===data.id&&deal.attribution.variantId===variantId);
    if(expected.length!==3||!Array.isArray(data.dealBars)||data.dealBars.length!==3)return false;
    const host=block.querySelector('.kaching-bundles__block[data-deal-block-id]');
    if(!host||host.getAttribute('data-deal-block-id')!==data.id||(host.getAttribute('data-ab-test-variant-id')||'')!==(data.abTestVariantId||''))return false;
    const cards=[...host.querySelectorAll('.kaching-bundles__bar[data-deal-bar-id]')];
    if(cards.length!==3||new Set(cards.map(card=>card.getAttribute('data-deal-bar-id'))).size!==3)return false;
    for(const deal of expected){
      const bars=data.dealBars.filter(bar=>bar.id===deal.attribution.barId);
      if(bars.length!==1||bars[0].quantity!==deal.quantity||bars[0].discountType!=='specific'||
        Math.round(Number(bars[0].discountValue)*100)!==deal.priceCents)return false;
      if(!cards.some(card=>card.getAttribute('data-deal-bar-id')===deal.attribution.barId))return false;
    }
    const selected=block.querySelector('input[type="radio"]:checked');
    return !!selected&&expected.some(deal=>deal.attribution.barId===selected.value);
  }catch{return false;}
}

export async function bootKachingBridge(policy, env=window) {
  if (!policy?.enabled) return null;
  validatePolicy(policy);
  const doc=env.document, form=doc.getElementById('oferta'), drawer=doc.getElementById('dr');
  const overlay=doc.getElementById('ov'), submit=doc.getElementById('cta1'), pay=doc.getElementById('go');
  if (!form || !drawer || !overlay || !submit || !pay) throw new Error('Falta el formulario o carrito de CURVES');
  const routeRoot=env.Shopify?.routes?.root || '/';
  const nativeFetch=env.fetch.bind(env), cleanups=[],addonReferences=new WeakMap();
  let current=null, pending=null, selectedQuantity=null, paying=false, queue=Promise.resolve();
  let lastAcknowledged=0, expectedPrice=null, destroyed=false, generation=0;
  let nativeFlights=0,recoveryRequired=false,recoveryScheduled=false,uncertainWrite=false;
  let selectionRestorer=null,pendingSelectionReadTicket=null,initialReadFailed=false;
  let cartReturnRequested=false,reopenReadFailed=false;
  let offerReady=false,offerTimedOut=false,offerTimer=null,offerReloading=false;
  let addonBatch=null;
  const offerStatus=doc.getElementById('gzNativeOfferStatus'),offerMessage=doc.getElementById('gzNativeOfferMessage');
  const offerReload=doc.getElementById('gzNativeOfferReload');
  try{cartReturnRequested=policy.market==='MX'&&new URL(env.location.href).searchParams.get('gz_cart')==='1';}catch{}
  const notice=doc.createElement('p'); notice.id='gzKachingNotice'; notice.setAttribute('role','status');
  notice.hidden=true; form.append(notice);
  const cartNotice=doc.createElement('p'); cartNotice.className='cart-price-notice';
  cartNotice.setAttribute('role','status'); cartNotice.hidden=true; drawer.querySelector('.ft')?.prepend(cartNotice);
  const editCart=doc.createElement('button');editCart.type='button';editCart.id='gzChangeTreatment';editCart.textContent='Cambiar tratamiento';
  editCart.style.cssText='display:block;margin:8px auto 0;min-height:44px;background:none;border:0;text-decoration:underline;font:inherit;font-size:12px;cursor:pointer';drawer.querySelector('.ft')?.append(editCart);
  // Returning customers recover inside this drawer without leaving the landing.
  const legacyCartRecovery=doc.createElement('p');legacyCartRecovery.id='gzKachingLegacyRecovery';
  legacyCartRecovery.hidden=true;legacyCartRecovery.style.margin='12px 0 0';
  const legacyCartButton=doc.createElement('button');legacyCartButton.type='button';legacyCartButton.id='gzReviewTreatment';
  legacyCartButton.textContent='Revisar mi tratamiento';
  legacyCartButton.style.cssText='display:inline-block;min-height:44px;padding:12px 16px;box-sizing:border-box;border:1px solid currentColor;border-radius:10px;color:inherit;background:none;text-align:center;font:inherit;font-weight:600;cursor:pointer';
  legacyCartRecovery.append(legacyCartButton);
  const recoveryAnchor=submit.closest('add-to-cart-component')||submit;
  recoveryAnchor.after(legacyCartRecovery);
  // A lost response never authorizes another write. Offer an explicit page reload instead.
  const reloadCartRecovery=doc.createElement('p');reloadCartRecovery.id='gzKachingReloadRecovery';
  reloadCartRecovery.hidden=true;reloadCartRecovery.style.margin='12px 0 0';
  const reloadCart=doc.createElement('button');reloadCart.type='button';reloadCart.id='gzKachingReloadCart';
  reloadCart.textContent='Volver a cargar el carrito';
  reloadCart.style.cssText='min-height:44px;padding:12px 16px;border:1px solid currentColor;border-radius:10px;background:transparent;color:inherit;font-weight:600';
  reloadCartRecovery.append(reloadCart);recoveryAnchor.after(reloadCartRecovery);
  const reloadDrawer=doc.createElement('button');reloadDrawer.type='button';reloadDrawer.id='gzReloadOpenCart';
  reloadDrawer.textContent='Volver a cargar el carrito';reloadDrawer.hidden=true;
  reloadDrawer.style.cssText=reloadCart.style.cssText;cartNotice.after(reloadDrawer);
  const pendingView=createPendingCartPresentation({doc,drawer,onReload:()=>env.location.reload()});
  cleanups.push(()=>pendingView.destroy());
  const reselect=doc.createElement('button');reselect.type='button';reselect.hidden=true;
  reselect.textContent='Volver a elegir tratamiento';
  reselect.style.cssText='margin:6px auto;min-height:44px;display:block;border:1px solid currentColor;border-radius:8px;background:transparent;color:inherit;padding:8px 12px';
  // Inline display must not override the native hidden state.
  reselect.style.removeProperty('display');cartNotice.after(reselect);
  const sticky=doc.getElementById('stt');if(sticky)sticky.textContent='Elige tu tratamiento';
  const mainRow=doc.getElementById('dimg')?.closest('.line');
  const removeMain=mainRow?doc.createElement('button'):null;
  function removalButton(button,label){
    button.type='button';button.textContent=label;button.dataset.gzRemoveTreatment='true';
    button.style.cssText='display:block;min-height:44px;padding:0;text-align:left;background:none;border:0;text-decoration:underline;font:inherit;font-size:12px;cursor:pointer';
  }
  if(removeMain){removalButton(removeMain,'Quitar tratamiento');removeMain.id='gzRemoveTreatment';mainRow.querySelector('div')?.append(removeMain);}
  const money=cents=>new Intl.NumberFormat(policy.market==='MX'?'es-MX':'es-ES',
    {style:'currency',currency:policy.market==='MX'?'MXN':'EUR'}).format(cents/100);
  function renderedSelection(){
    const selection=readRenderedOffer(form,policy);
    const deal=policy.deals.find(item=>item.attribution?.testId===selection.testId&&
      item.attribution.variantId===selection.variantId&&item.attribution.barId===selection.barId);
    if(!deal)throw new Error('No se confirmó la oferta seleccionada.');
    return {selection,deal};
  }
  function reflectSelection(){
    try{const {deal}=renderedSelection();publishTreatmentContext(doc,env,deal,policy);if(sticky)sticky.textContent=treatmentPresentation(deal).title+' · '+money(deal.priceCents);}
    catch{if(sticky)sticky.textContent='Elige tu tratamiento';}
  }
  const say=(text, cart=false)=>{const el=cart?cartNotice:notice;el.textContent=text;el.hidden=!text;};
  const listen=(el,name,fn,options)=>{el.addEventListener(name,fn,options);cleanups.push(()=>el.removeEventListener(name,fn,options));};
  listen(reloadCart,'click',()=>env.location.reload());
  listen(reloadDrawer,'click',()=>env.location.reload());
  if(offerReload)listen(offerReload,'click',()=>{
    if(!offerTimedOut||offerReloading)return;
    offerReloading=true;offerReload.disabled=true;env.location.reload();
  });
  function updateOfferReadiness(){
    offerReady=nativeOfferReady(form,policy);
    if(offerReady){
      if(offerTimer!==null){env.clearTimeout(offerTimer);offerTimer=null;}
      offerTimedOut=false;
    }else if(offerTimer===null&&!offerTimedOut&&!destroyed){
      offerTimer=env.setTimeout(()=>{offerTimer=null;if(destroyed)return;offerTimedOut=true;updateOfferReadiness();reflect();},10000);
    }
    if(offerStatus)offerStatus.hidden=offerReady;
    if(offerMessage){const text=offerTimedOut?'Los tratamientos están tardando en cargar. Puedes volver a cargar la oferta.':'Cargando tratamientos…';if(offerMessage.textContent!==text)offerMessage.textContent=text;}
    if(offerReload)offerReload.hidden=offerReady||!offerTimedOut;
  }
  cleanups.push(()=>{if(offerTimer!==null)env.clearTimeout(offerTimer);});
  const url=part=>routeRoot.replace(/\/?$/, '/')+part;
  const delay=ms=>new Promise(resolve=>env.setTimeout(resolve,ms));
  const request=(part,body)=>requestShopifyCart(nativeFetch,url(part),body);
  const read=async()=>validateCart(await request('cart.js'));
  function reflect() {
    reloadCartRecovery.hidden=!(initialReadFailed||uncertainWrite||reopenReadFailed);
    reloadDrawer.hidden=!reopenReadFailed;
    const blocked=!current || !!pending || paying || recoveryRequired || nativeFlights>0 || uncertainWrite;
    pay.disabled=blocked || !current?.items.length || hasLegacyMain(current,policy);
    legacyCartButton.disabled=blocked;
    for(const button of doc.querySelectorAll('[data-gz-remove-treatment]'))button.disabled=blocked;
    // Do not disable during capture of the first click: that would cancel its native submit.
    submit.disabled=!current || recoveryRequired || paying || current&&hasLegacyMain(current,policy) || selectionRestorer?.state().busy || selectionRestorer?.state().failed || !offerReady;
    const addonBlocked=!current||!!pending||(paying&&!addonBatch)||recoveryRequired||nativeFlights>0||uncertainWrite;
    for(const input of doc.querySelectorAll('.addon input,.fbt-i input'))input.disabled=addonBlocked;
    for(const button of doc.querySelectorAll('#fbtDos,#drDos'))button.disabled=addonBlocked;
    drawer.dataset.gzKachingState=recoveryRequired?'recovering':pending?'adding':paying?'updating':current?'ready':'loading';
  }
  function render(cart) {
    current=cart;
    const legacyPresent=hasLegacyMain(cart,policy);legacyCartRecovery.hidden=!legacyPresent;
    if(legacyPresent){
      const message='Tu carrito conserva un tratamiento anterior. Quítalo aquí y elige la oferta que prefieras; tus complementos se conservan.';
      say(message);say(message,true);
    }
    const summary=cartSummary(cart,policy);
    const set=(selector,text)=>{const el=doc.querySelector(selector);if(el)el.textContent=text;};
    const mainDeal=summary.mains[0]?matchingDeal(summary.mains[0],policy):null;
    if(removeMain)removeMain.hidden=!summary.mains.length;
    const presentation=mainDeal?treatmentPresentation(mainDeal):null;
    set('#dt',presentation?.title||'Tu pedido');
    set('#ds',presentation?.subtitle||summary.quantity+' '+(summary.quantity===1?'frasco':'frascos')+' de CURVES');
    set('#dp',money(summary.mainCents));
    set('#tot',money(summary.totalCents));
    set('#dold',summary.savingsCents?money(summary.totalCents+summary.savingsCents):'');
    set('#dah',summary.savingsCents?'Ahorras '+money(summary.savingsCents):'');
    renderPackSavings(doc,packSavingsSummary(summary,policy));
    const image=doc.getElementById('dimg');
    if(image?.closest('.line'))image.closest('.line').hidden=!summary.mains.length;
    if(image && summary.mains.length){
      const packImage=packPresentationImage(summary,policy);
      if(packImage)image.src=packImage;image.alt='CURVES · '+summary.quantity+' '+(summary.quantity===1?'frasco':'frascos');
    }
    const gifts=doc.getElementById('dreg');
    if(gifts){gifts.replaceChildren();for(const line of summary.gifts){
      const row=doc.createElement('div');row.className='rg';
      const img=doc.createElement('img');img.width=40;img.height=40;img.alt='';if(line.image)img.src=line.image;
      const title=doc.createElement('span');title.textContent=policy.giftLabels?.[String(line.variant_id??line.id)] || line.product_title || line.title || 'Regalo';
      const price=doc.createElement('b');price.textContent=line.final_line_price===0?'GRATIS':money(line.final_line_price);
      row.append(img,title,price);gifts.append(row);
    }gifts.hidden=!summary.gifts.length;}
    const guide=doc.querySelector('.dgift');if(guide)guide.hidden=mainDeal?.guideIncluded!==true;
    let other=doc.getElementById('gzKachingOtherLines');
    if(!other){other=doc.createElement('div');other.id='gzKachingOtherLines';doc.getElementById('dreg')?.after(other);}
    other.replaceChildren();
    for(const line of cart.items.filter(line=>!summary.mains.includes(line)&&!summary.gifts.includes(line)&&
      !policy.addonVariantIds.map(String).includes(String(line.variant_id??line.id)))){
      const row=doc.createElement('div');row.className='line';
      const title=doc.createElement('span');title.textContent=line.quantity+' × '+(line.product_title||line.title);
      const price=doc.createElement('b');price.textContent=money(line.final_line_price);row.append(title,price);
      if(String(line.variant_id??line.id)===String(policy.curvesVariant)){
        const button=doc.createElement('button');removalButton(button,'Quitar tratamiento anterior');
        const intent=captureTreatmentRemoval(cart,policy,[line.key]);
        button.addEventListener('click',()=>{void removeTreatment(intent);});row.append(button);
      }
      other.append(row);
    }
    for(const addon of policy.addons){
      const {quantity,totalCents,originalCents,unitCents}=addonPriceSummary(standaloneAddonLines(cart,addon.id,policy),addon.offerCents);
      for(const box of doc.querySelectorAll('.addon[data-k="'+addon.key+'"],.fbt-i[data-k="'+addon.key+'"]')){
        box.hidden=false;box.classList.toggle('on',quantity>0);const input=box.querySelector('input');if(input)input.checked=quantity>0;
        const price=box.querySelector('.apr b,.fbt-p b');if(price)price.textContent=money(totalCents);
        const original=box.querySelector('.apr del,.fbt-p del');
        if(original){
          if(!addonReferences.has(box))addonReferences.set(box,original.textContent);
          original.textContent=quantity?(originalCents>totalCents?money(originalCents):''):addonReferences.get(box);
        }
        let badge=box.querySelector('.gz-addon-quantity');
        if(!badge){badge=doc.createElement('span');badge.className='gz-addon-quantity';badge.style.cssText='white-space:nowrap;font-size:inherit;font-weight:700';box.querySelector('.atx b,.fbt-t b')?.append(badge);}
        badge.textContent=quantity>1?' ×'+quantity:'';
        let unit=box.querySelector('.gz-addon-unit');
        if(!unit){unit=doc.createElement('small');unit.className='gz-addon-unit';unit.style.cssText='font-size:10px;line-height:1.2';box.querySelector('.apr,.fbt-p')?.append(unit);}
        unit.textContent=quantity>1&&unitCents!==null?money(unitCents)+' c/u':'';
      }
    }
    reselect.hidden=true;
    if(hasKachingMain(cart,policy)&&!hasLegacyMain(cart,policy)){
      try{assertKachingCheckout(cart,policy);}catch{reselect.hidden=false;}
    }
    expectedPrice=cart.total_price;reflect();
    const all=policy.addons.every(addon=>standaloneAddonLines(cart,addon.id,policy).some(line=>line.quantity>0));
    for(const button of doc.querySelectorAll('#fbtDos,#drDos')){
      button.hidden=policy.addons.length<2;button.textContent=all?'✓ Curvas 360 completo':policy.market==='MX'?'Agregar los dos · Curvas 360':'Añadir los dos · Curvas 360';
      button.classList.toggle('on',all);
    }
  }
  function open(){if(!current)return;render(current);if(!pending)pendingView.clear();drawer.classList.add('on');overlay.classList.add('on');doc.body.style.overflow='';doc.body.style.overflow='hidden';}
  function close(){drawer.classList.remove('on');overlay.classList.remove('on');doc.body.style.overflow='';}
  function dismissDrawer(){if(pending)pending.drawerDismissed=true;close();}
  function openReturnedCart(){
    if(!cartReturnRequested||!current||selectionRestorer?.state().busy)return;
    // A return from Shopify only displays the freshly read cart; it never adds or reconciles.
    if(!current.items.length)say('Tu carrito está vacío. Elige tu tratamiento para continuar.',true);
    open();cartReturnRequested=false;
    try{
      const address=new URL(env.location.href);address.searchParams.delete('gz_cart');
      env.history.replaceState(env.history.state,'',address.pathname+address.search+address.hash);
    }catch{} // Opening succeeds even when the browser disallows replacing history.
  }
  async function reconcile(cart,allowWrite=true){
    const plan=planKachingCart(cart,policy);
    if(!plan) return cart;
    const currentCodes=cart.discount_codes.map(d=>d.code).join(',');
    if(Object.keys(plan.updates).length || currentCodes!==plan.discountCodes.join(',')){
      if(!allowWrite)throw new Error('El carrito necesita una revisión. Vuelve a cargarlo antes de continuar.');
      return validateCart(await request('cart/update.js',{updates:plan.updates,discount:plan.discountCodes.join(',')}));
    }
    return cart;
  }
  function serialized(task){const work=queue.catch(()=>{}).then(task);queue=work;return work;}
  function recover(message,uncertain=false,restoreTicket=null){
    if(uncertain)uncertainWrite=true;
    if(restoreTicket!==null)pendingSelectionReadTicket=restoreTicket;
    recoveryRequired=true;current=null;reflect();
    if(message)say(message);
    if(recoveryScheduled||destroyed)return;
    recoveryScheduled=true;
    void serialized(async()=>{
      const ticketForRead=pendingSelectionReadTicket;
      pendingSelectionReadTicket=null;
      try{
        const fresh=await read();
        // A newer history return needs its own read, after this request began.
        if(destroyed||pendingSelectionReadTicket!==null)return;
        recoveryRequired=nativeFlights>0||uncertainWrite;
        render(fresh);
        if(ticketForRead!==null)selectionRestorer.offer(fresh,ticketForRead);
        openReturnedCart();
        if(uncertainWrite)say('La conexión no confirmó el resultado. Abre el carrito o recarga esta página antes de volver a añadir.');
      }catch(error){if(pendingSelectionReadTicket===null){current=null;say(error.message);}}
      finally{
        recoveryScheduled=false;
        if(pendingSelectionReadTicket!==null&&!destroyed)recover(undefined,false,pendingSelectionReadTicket);
        else reflect();
      }
    });
  }
  function cancelIntent(intent,message,uncertain=false){
    const visibleIntent=!!intent&&pending===intent;
    if(intent){intent.cancelled=true;env.clearTimeout(intent.timeout);if(pending===intent)pending=null;}
    if(visibleIntent)pendingView.fail(message);
    recover(message,uncertain);
  }
  async function acceptResponse(response,intent){
    const active=()=>intent && pending===intent && !intent.cancelled && !destroyed && intent.generation===generation;
    if(!active() || intent.received)return;
    const added=Array.isArray(response?.items)?response.items:[response];
    if(!added.some(line=>line&&ownsMain(line,policy)))return;
    intent.received=true;
    await serialized(async()=>{
      try{
        let cart,attributes,lastError;
        for(let i=0;i<5;i++){
          if(!active())return;
          cart=await read();
          if(!active())return;
          try{attributes=acknowledgeNativeAdd(intent.before,cart,response,policy,intent.expectedQuantity);break;}
          catch(error){lastError=error;if(i<4)await delay(120);}
        }
        if(!attributes)throw lastError;
        if(!active())return;
        const replacement=planReplacement(intent.before,cart,response,policy);
        cart=validateCart(await request('cart/update.js',{updates:replacement.updates,attributes}));
        if(!active())return;
        cart=await reconcile(cart);
        if(!active())return;
        assertBundleCharges(cart,policy);
        // Kaching's own add response and persisted quantities have now been acknowledged.
        pending=null;env.clearTimeout(intent.timeout);lastAcknowledged=Date.now();say('');say('',true);current=cart;
        if(intent.drawerDismissed){render(cart);pendingView.clear();}else open();
      }catch(error){cancelIntent(intent,error.message,error.uncertainCartWrite);}
    });
  }
  const isAdd=resource=>{try{const u=new URL(typeof resource==='string'?resource:resource.url,env.location.href);
    return u.origin===env.location.origin && /\/cart\/add(?:\.js)?$/.test(u.pathname);}catch{return false;}};
  cleanups.push(observeNativeAdds(env,{isAdd,getIntent:()=>pending,
    onStart(){nativeFlights++;},onResponse:acceptResponse,
    onFailure(intent,error,uncertain){cancelIntent(intent,error.message,uncertain);},
    onSettled(){nativeFlights--;if(recoveryRequired)recover();else reflect();}}));
  function reopenSelection(){
    const {selection,deal}=renderedSelection();
    const data=JSON.parse(form.querySelector('kaching-bundles-block[deal-block]').getAttribute('deal-block'));
    const bar=data.dealBars?.find(item=>item.id===selection.barId);
    if(!bar||bar.discountType!=='specific'||bar.quantity!==deal.quantity||
      Math.round(Number(bar.discountValue)*100)!==deal.priceCents)return null;
    return {...selection,quantity:bar.quantity,priceCents:deal.priceCents};
  }
  function reopenTreatment(selection){
    const ticket=generation;paying=true;reflect();open(); // Show confirmed contents immediately; lock all writes until fresh GET.
    void serialized(async()=>{
      try{
        const fresh=await read();if(destroyed||ticket!==generation)return;
        const selectedNow=reopenSelection();
        if(!canReopenTreatment(fresh,policy,selection)||!selectedNow||
          ['mainVariant','testId','variantId','barId','quantity','priceCents'].some(key=>selectedNow[key]!==selection[key]))
          throw new Error('El carrito o tu selección cambió. Vuelve a cargarlo antes de continuar.');
        say('');say('',true);render(fresh);
      }catch(error){
        if(destroyed||ticket!==generation)return;
        current=null;recoveryRequired=true;reopenReadFailed=true;
        const message=error.message+' Vuelve a cargar el carrito para continuar.';
        say(message);say(message,true);
      }finally{if(!destroyed&&ticket===generation){paying=false;reflect();}}
    });
  }
  function begin(event){
    if(event.type==='click' && !event.target.closest('#cta1'))return;
    if(pending && event.type==='submit' && pending.allowSubmit && event.submitter===submit && Date.now()-pending.started<150){
      pending.allowSubmit=false;return; // click and its immediate submit belong to the same native gesture
    }
    if(!offerReady||selectionRestorer?.state().busy||selectionRestorer?.state().failed){event.preventDefault();event.stopImmediatePropagation();return;}
    if(pending || paying || !current || recoveryRequired || nativeFlights>0 || Date.now()-lastAcknowledged<800){event.preventDefault();event.stopImmediatePropagation();if(current&&!pending)open();return;}
    if(hasLegacyMain(current,policy)){event.preventDefault();event.stopImmediatePropagation();say('Tu carrito conserva el tratamiento anterior. Revísalo en el carrito antes de elegir otra oferta.');return;}
    if(['ajax','horizon'].includes(policy.formTransport)){
      try{const selectedDeal=renderedSelection().deal;selectedQuantity=selectedDeal.quantity;
        if(!canSelectOfferBeforeAdd(current,selectedDeal,policy)){
          event.preventDefault();event.stopImmediatePropagation();
          say('Tu carrito conserva otro tratamiento. Puedes terminarlo o quitarlo antes de elegir este.');open();return;
        }}
      catch(error){event.preventDefault();event.stopImmediatePropagation();say(error.message);return;}
    }
    let reopen=null;try{reopen=reopenSelection();}catch{}
    if(!uncertainWrite&&reopen&&canReopenTreatment(current,policy,reopen)){
      event.preventDefault();event.stopImmediatePropagation();reopenTreatment(reopen);return;
    }
    const intent={before:current,expectedQuantity:selectedQuantity,generation,received:false,started:Date.now(),allowSubmit:event.type==='click'};
    pending=intent;reflect();say('');
    pendingView.pending();open();
    intent.timeout=env.setTimeout(()=>{if(pending===intent)cancelIntent(intent,'Todavía no se confirmó el resultado. Espera mientras revisamos tu carrito.');},15000);
    // No preventDefault on the first gesture: Kaching and Shopify own the real add.
  }
  listen(form,'click',begin,true);listen(form,'submit',begin,true);
  if(['ajax','horizon'].includes(policy.formTransport)){
    const attributionInputs=installOfferAttributionInputs({env,form,readSelection:()=>renderedSelection().selection});
    cleanups.push(()=>attributionInputs.destroy());
    const transport=installProductFormAjax({env,form,mode:()=>policy.formTransport,nativeRequestActive:()=>nativeFlights>0,
      canSubmit:()=>!!pending&&!!current&&!paying&&!recoveryRequired&&!uncertainWrite,
      decoratePayload:payload=>appendOfferAttribution(payload,renderedSelection().selection),
      onError:error=>cancelIntent(pending,error.message,error.uncertainCartWrite)});
    cleanups.push(()=>transport.destroy());
  }
  selectionRestorer=createCartSelectionRestorer({policy,form,env,onChange:()=>{
    if(selectionRestorer?.state().failed)say('Tu carrito conserva tu tratamiento. Vuelve a elegirlo para continuar.');
    reflectSelection();reflect();openReturnedCart();
  }});
  cleanups.push(()=>selectionRestorer.destroy());
  const initialSelectionTicket=selectionRestorer.start();
  const explicitSelection=event=>{
    if(!event.isTrusted||!event.target.closest?.('.kaching-bundles__bar')||event.target.closest('a'))return;
    if(event.type==='keydown'&&!['Enter',' ','ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(event.key))return;
    const wasFailed=selectionRestorer.state().failed;selectionRestorer.userSelection();if(wasFailed)say('');
  };
  for(const name of ['pointerdown','click','keydown'])listen(form,name,explicitSelection,true);
  const connected=new WeakSet();
  function attachWidgetEvents(){for(const block of form.querySelectorAll('kaching-bundles-block')){
    retainNativeOfferSelection(block,policy,form.querySelector('input[name="id"]')?.value);
    if(connected.has(block))continue;connected.add(block);
    listen(block,'variants-changed',event=>{selectedQuantity=selectedMainQuantity(event.detail,policy);selectionRestorer.retry();reflectSelection();updateOfferReadiness();reflect();});
    listen(block,'deal-bar-selected',()=>{selectionRestorer.retry();reflectSelection();updateOfferReadiness();reflect();});
  }selectionRestorer.retry();reflectSelection();updateOfferReadiness();reflect();}
  attachWidgetEvents();
  const observer=new env.MutationObserver(attachWidgetEvents);observer.observe(form,{childList:true,subtree:true,attributes:true,attributeFilter:['data-initialized','deal-block','data-deal-block-id','data-ab-test-variant-id','data-deal-bar-id','config','checked','class']});
  cleanups.push(()=>observer.disconnect());
  listen(doc.getElementById('cta2'),'click',event=>{event.preventDefault();form.scrollIntoView({behavior:'smooth'});});
  listen(reselect,'click',()=>{close();form.scrollIntoView({behavior:'smooth',block:'start'});
    say('Vuelve a seleccionar tu tratamiento y pulsa el botón para confirmar su cantidad y precio.');
    form.querySelector('kaching-bundles-block input[type="radio"]:checked')?.focus({preventScroll:true});});
  listen(doc.getElementById('drx'),'click',dismissDrawer);listen(overlay,'click',dismissDrawer);
  listen(editCart,'click',()=>{close();form.scrollIntoView({behavior:'smooth',block:'start'});});
  listen(legacyCartButton,'click',()=>{if(current&&!pending&&!paying&&!recoveryRequired&&!nativeFlights&&!uncertainWrite)open();});
  if(removeMain)listen(removeMain,'click',()=>{
    if(!current)return;
    const keys=current.items.filter(line=>ownsMain(line,policy)).map(line=>line.key);
    if(keys.length)void removeTreatment(captureTreatmentRemoval(current,policy,keys));
  });
  listen(doc,'keydown',event=>{if(event.key==='Escape')dismissDrawer();});
  function removeTreatment(intent){
    if(!current||pending||paying||recoveryRequired||nativeFlights>0||uncertainWrite||destroyed)return;
    paying=true;reflect(); // Synchronous lock also rejects a rapid second click.
    selectionRestorer?.userSelection();
    return serialized(async()=>{
      let wrote=false;
      try{
        const before=await read(),plan=planTreatmentRemoval(before,policy,intent);
        wrote=true;
        let cart=verifyTreatmentRemoval(before,await request('cart/update.js',{updates:plan.updates}),plan);
        // All removal/cascade changes must settle in this one POST; never replay a write.
        if(!hasLegacyMain(cart,policy))cart=await reconcile(cart,false);
        say('');say('',true);render(cart);
        if(!hasLegacyMain(cart,policy))say(cart.items.length?'Tratamiento quitado. Tus otros artículos se conservaron.':'Tu carrito está vacío. Elige tu tratamiento para continuar.',true);
      }catch(error){
        if(wrote)error.uncertainCartWrite=true;
        say(error.message,true);recover(error.message,error.uncertainCartWrite);
      }finally{paying=false;reflect();}
    });
  }
  function addonFeedback(targets,busy){
    for(const addon of policy.addons){
      const affected=!!targets&&Object.hasOwn(targets,addon.id);
      for(const box of doc.querySelectorAll('.addon[data-k="'+addon.key+'"],.fbt-i[data-k="'+addon.key+'"]')){
        if(affected){const checked=targets[addon.id]>0;box.classList.toggle('on',checked);
          const input=box.querySelector('input');if(input)input.checked=checked;}
        box.setAttribute('aria-busy',busy&&affected?'true':'false');
        let status=box.querySelector('.gz-addon-pending');
        if(busy&&affected&&!status){status=doc.createElement('div');status.className='gz-addon-pending';
          status.style.cssText='font-size:11px;line-height:1.25';status.setAttribute('role','status');
          (box.querySelector('.atx,.fbt-t')||box).append(status);}
        if(status){status.hidden=!(busy&&affected);if(busy&&affected)status.textContent='Actualizando…';}
      }
    }
    const counts={...addonCounts(current||addonBatch?.lastConfirmed),...(targets||{})};
    const all=policy.addons.every(addon=>counts[addon.id]>0);
    for(const button of doc.querySelectorAll('#fbtDos,#drDos')){
      button.setAttribute('aria-busy',busy?'true':'false');button.classList.toggle('on',all);
      button.textContent=all?'✓ Curvas 360 completo':policy.market==='MX'?'Agregar los dos · Curvas 360':'Añadir los dos · Curvas 360';
    }
  }
  const addonCounts=cart=>Object.fromEntries(policy.addons.map(addon=>[addon.id,
    standaloneAddonLines(cart,addon.id,policy).reduce((sum,line)=>sum+line.quantity,0)]));
  const pendingAddonTargets=batch=>Object.fromEntries([...batch.desired].map(([variant,intent])=>[variant,intent.preview]));
  function setAddons(targets){
    if(!current||pending||(paying&&!addonBatch)||recoveryRequired||uncertainWrite||nativeFlights>0||destroyed)return;
    const previewTargets=typeof targets==='function'?targets(current):targets;
    const batch=addonBatch||(addonBatch={pending:new Map(),desired:new Map(),lastConfirmed:current,generation,work:null});
    for(const [variant,preview] of Object.entries(previewTargets)){
      const intent={preview,resolve:typeof targets==='function'?cart=>targets(cart)[variant]:()=>preview};
      batch.pending.set(variant,intent);batch.desired.set(variant,intent);
    }
    paying=true;addonFeedback(pendingAddonTargets(batch),true);reflect(); // Lock and visible intent are synchronous.
    if(batch.work)return batch.work;
    batch.work=serialized(async()=>{
      let failed=false;
      const active=()=>{if(addonBatch!==batch||generation!==batch.generation||recoveryRequired||uncertainWrite||nativeFlights>0||pending||destroyed)throw new Error('Vuelve a cargar tu carrito antes de continuar.');};
      try{
        while(batch.pending.size){
          active();let cart=await read(),updates={},adds=[];active();
          // New toggles arriving during the GET join this request; later toggles stay for the next pass.
          const intents=new Map(batch.pending);batch.pending.clear();
          for(const [variant,intent] of intents){
            const plan=planAddon(cart,variant,intent.resolve(cart),policy);Object.assign(updates,plan.updates);adds.push(...plan.adds);
          }
          if(Object.keys(updates).length){cart=validateCart(await request('cart/update.js',{updates}));active();}
          if(adds.length){await request('cart/add.js',{items:adds});active();cart=await read();active();}
          cart=await reconcile(cart);active();batch.lastConfirmed=cart;render(cart);say('',true);
          for(const [variant,intent] of intents)if(batch.desired.get(variant)===intent)batch.desired.delete(variant);
          addonFeedback(pendingAddonTargets(batch),true);
        }
      }catch(error){
        failed=true;batch.pending.clear();batch.desired.clear();
        if(!destroyed){say(error.message,true);recover(error.message,error.uncertainCartWrite);}
      }finally{
        addonFeedback(failed?addonCounts(batch.lastConfirmed):null,false);addonBatch=null;paying=false;reflect();
      }
    });
    return batch.work;
  }
  for(const addon of policy.addons){
    for(const input of doc.querySelectorAll('.addon[data-k="'+addon.key+'"] input,.fbt-i[data-k="'+addon.key+'"] input')){
      listen(input,'change',()=>{const checked=input.checked;
        // ON means present, preserving a quantity already added by another cart action.
        void setAddons(fresh=>({[addon.id]:checked?Math.max(1,addonCounts(fresh)[addon.id]):0}));
      });
    }
  }
  for(const button of doc.querySelectorAll('#fbtDos,#drDos'))listen(button,'click',()=>{
    if(!current||pending||(paying&&!addonBatch)||recoveryRequired||nativeFlights>0||uncertainWrite)return;
    const counts=addonCounts(current),desired=addonBatch?pendingAddonTargets(addonBatch):{};
    const remove=policy.addons.every(addon=>(desired[addon.id]??counts[addon.id])>0);
    void setAddons(fresh=>bothAddonTargets(fresh,policy,remove));
  });
  for(const el of doc.querySelectorAll('#fbt,#addonH'))el.hidden=!policy.addons.length;
  listen(pay,'click',()=>{if(paying||pending||!current||recoveryRequired||nativeFlights>0)return;const reviewed=expectedPrice;paying=true;reflect();
    void serialized(async()=>{try{
      const cart=await reconcile(await read());
      assertKachingCheckout(cart,policy);
      const currentSummary=cartSummary(cart,policy);
      if(currentSummary.gifts.some(line=>line.final_line_price!==0))throw new Error('Un regalo no aparece gratis. Revisa el carrito antes de pagar.');
      render(cart);
      if(cart.total_price>reviewed){say('El precio se actualizó. Revisa el total antes de continuar.',true);return;}
      say('',true);env.location.assign(checkoutURL(env.location.href,routeRoot));
    }catch(error){say(error.message,true);recover(error.message,error.uncertainCartWrite);}finally{paying=false;reflect();}});
  });
  listen(env,'pageshow',event=>{if(!event.persisted)return;generation++;if(pending){pending.cancelled=true;env.clearTimeout(pending.timeout);}pending=null;paying=false;selectedQuantity=null;pendingView.clear();close();const ticket=selectionRestorer.start();recover(undefined,false,ticket);});
  submit.disabled=true;reflect();
  try{const cart=await readInitialCart(read,delay);if(!destroyed&&selectionRestorer.state().epoch===initialSelectionTicket){render(cart);selectionRestorer.offer(cart,initialSelectionTicket);reflect();openReturnedCart();}}
  catch(error){if(!destroyed&&selectionRestorer.state().epoch===initialSelectionTicket){
    initialReadFailed=true;
    say('No pudimos cargar tu carrito. Vuelve a cargarlo para continuar.');reflect();
  }}
  return {destroy(){destroyed=true;generation++;if(pending)env.clearTimeout(pending.timeout);cleanups.reverse().forEach(fn=>fn());close();},refresh:async()=>render(await read())};
}

if(typeof window!=='undefined' && window.GZ_KACHING_CONFIG?.enabled){
  void bootKachingBridge(window.GZ_KACHING_CONFIG).catch(error=>{
    const form=document.getElementById('oferta');if(!form)return;
    const message=document.createElement('p');message.setAttribute('role','alert');message.textContent=error.message;
    form.append(message);
  });
}

/* GLAZED V4: presentation only; Kaching retains its inputs, handlers and prices. */
(() => {
  const policy = window.GZ_KACHING_CONFIG;
  const form = document.querySelector('.gz-kaching-native-form');
  if (!form || !policy?.enabled || !['MX', 'ES'].includes(policy.market) || !Array.isArray(policy.deals)) return;
  if (String(form.querySelector('input[name="id"]')?.value) !== String(policy.curvesVariant)) return;
  if (form.dataset.gzVisual === 'v4') return;
  form.dataset.gzVisual = 'v4';
  const locale = policy.market === 'MX' ? 'es-MX' : 'es-ES';
  const currency = policy.market === 'MX' ? 'MXN' : 'EUR';
  const money = new Intl.NumberFormat(locale, {style:'currency', currency, minimumFractionDigits:policy.market === 'MX' ? 0 : 2, maximumFractionDigits:2});
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.67);
  const group = parts.find(p => p.type === 'group')?.value;
  const decimal = parts.find(p => p.type === 'decimal')?.value;
  const cents = node => {
    if (!node) return null;
    let value = node.textContent.trim().replace(/(?:MXN|EUR|[$€]|\s)/g, '');
    if (group) value = value.split(group).join('');
    if (decimal && decimal !== '.') value = value.split(decimal).join('.');
    return /^\d+(?:\.\d{1,2})?$/.test(value) ? Math.round(Number(value) * 100) : null;
  };
  const text = (node, value) => { if (node.textContent !== value) node.textContent = value; };
  const images = new WeakMap();
  const badges = new WeakMap();
  const descriptions = new WeakMap();
  const productLink = gift => {
    const anchors = [...gift.querySelectorAll('a[href]')];
    if (!anchors.length) return null;
    if (anchors.length !== 1 || !anchors[0].matches('.kaching-bundles__free-gift__link')) return false;
    const anchor = anchors[0], href = anchor.getAttribute('href');
    if (anchor.hasAttribute('role') && anchor.getAttribute('role') !== 'link') return false;
    if (anchor.hasAttribute('tabindex')) return false;
    try {
      const origin = policy.market === 'MX' ? 'https://glazed.com.mx' : 'https://glazed.com.es';
      const url = new URL(href, origin);
      if (url.origin !== origin || !/^\/products\/[^/]+$/.test(url.pathname)) return false;
    } catch (_) { return false; }
    return anchor;
  };
  const restore = bar => {
    bar.querySelector('.gz-v4-footer')?.remove();
    bar.querySelector('.gz-v4-saving')?.remove();
    delete bar.dataset.gzIntegratedGift;
    const pricing = bar.querySelector('.kaching-bundles__bar-pricing');
    if (pricing) delete pricing.dataset.gzSimplePricing;
    const image = bar.querySelector('.kaching-bundles__bar-image'), oldImage = images.get(image);
    if (oldImage && image.getAttribute('src') === oldImage.applied) {
      if (oldImage.original === null) image.removeAttribute('src');
      else image.setAttribute('src', oldImage.original);
    }
    if (image) images.delete(image);
    const badge = bar.querySelector('.kaching-bundles__bar-label'), oldBadge = badges.get(badge);
    if (oldBadge !== undefined && badge.textContent === 'Para empezar') text(badge, oldBadge);
    if (badge) badges.delete(badge);
  };
  const giftName = gift => {
    const name = gift.querySelector('.kaching-bundles__free-gift__text')?.textContent.trim() || '';
    if (/brasier|sujetador/i.test(name)) return policy.market === 'MX' ? 'Brasier levantador' : 'Sujetador levantador';
    if (/crema/i.test(name) && /busto/i.test(name)) return 'crema de busto';
    return name.replace(/\bGRATIS\b/gi, '').replace(/^Regalo\s*[:·-]?\s*/i, '').trim();
  };
  const update = () => {
    for (const block of form.querySelectorAll('.kaching-bundles__block[data-deal-block-id][data-ab-test-variant-id]')) {
      let nativeData;
      try { nativeData = JSON.parse(block.closest('kaching-bundles-block')?.getAttribute('deal-block') || 'null'); } catch (_) { nativeData = null; }
      for (const bar of block.querySelectorAll('.kaching-bundles__bar[data-deal-bar-id]')) {
        const container = bar.querySelector(':scope > .kaching-bundles__bar-container');
        if (!container) continue;
        const deal = policy.deals.find(item => item.attribution?.testId === block.dataset.dealBlockId &&
          item.attribution.variantId === block.dataset.abTestVariantId && item.attribution.barId === bar.dataset.dealBarId);
        const pricing = bar.querySelector('.kaching-bundles__bar-main .kaching-bundles__bar-pricing');
        const nativeBar = nativeData?.dealBars?.find(item => item.id === bar.dataset.dealBarId);
        const currentPrice = cents(pricing?.querySelector(':scope > .kaching-bundles__bar-price'));
        const expected = Array.isArray(deal?.giftVariantIds) ? deal.giftVariantIds.length : 0;
        const gifts = [...container.querySelectorAll(':scope > .kaching-bundles__free-gift')];
        const giftQuantity = Array.isArray(nativeBar?.freeGifts) ? nativeBar.freeGifts.reduce((sum, item) => sum + (Number.isInteger(item.quantity) ? item.quantity : NaN), 0) : NaN;
        if (!deal || nativeData?.id !== block.dataset.dealBlockId || nativeData?.abTestVariantId !== block.dataset.abTestVariantId ||
          nativeBar?.quantity !== deal.quantity || currentPrice !== deal.priceCents || currentPrice <= 0 ||
          giftQuantity !== expected || gifts.length !== expected) { restore(bar); continue; }
        if (pricing) {
          const simple = [...pricing.children].every(node => node.matches('.kaching-bundles__bar-price,.kaching-bundles__bar-full-price,.gz-v4-saving'));
          if (simple) pricing.dataset.gzSimplePricing = 'true';
          else delete pricing.dataset.gzSimplePricing;
          const current = cents(pricing.querySelector(':scope > .kaching-bundles__bar-price'));
          const reference = cents(pricing.querySelector(':scope > .kaching-bundles__bar-full-price'));
          let saving = pricing.querySelector('.gz-v4-saving');
          if (simple && current === deal.priceCents && current > 0 && reference > current) {
            if (!saving) { saving = document.createElement('span'); saving.className = 'gz-v4-saving'; pricing.append(saving); }
            text(saving, 'Ahorras ' + money.format((reference - current) / 100));
          } else saving?.remove();
        }
        const links = gifts.map(productLink);
        const safe = gifts.length === expected && gifts.every((gift, index) => giftName(gift) && links[index] !== false &&
          !gift.matches('.kaching-bundles__free-gift--out-of-stock') &&
          [...gift.querySelectorAll('button,input,select,textarea,a[href],[role],[tabindex]')].every(node => node === links[index]));
        if (safe && gifts.length) bar.dataset.gzIntegratedGift = 'true';
        else delete bar.dataset.gzIntegratedGift;
        const names = safe ? gifts.map(giftName) : [];
        const guide = deal.guideIncluded === true;
        let heading = names.length ? (names.length === 1 ? 'Regalo' : names.length + ' regalos') : (guide ? 'Guía GRATIS' : '');
        const description = names.length ? names.join(' + ') + (guide ? ' + guía' : '') :
          guide ? (policy.market === 'MX' ? 'Curvas naturales en 30 días' : 'Tu ritual GLAZED de 30 días') : 'Envío gratis';
        // Unknown/interactive native gifts stay fully visible; do not duplicate their copy.
        let footer = container.querySelector(':scope > .gz-v4-footer');
        if (!footer) {
          footer = document.createElement('span'); footer.className = 'gz-v4-footer';
          const title = document.createElement('strong');
          const icon = document.createElement('span'); icon.className = 'i i-gift'; icon.setAttribute('aria-hidden', 'true');
          const label = document.createElement('span'); label.className = 'gz-v4-footer-title';
          title.append(icon, label);
          const detail = document.createElement('span'); detail.className = 'gz-v4-footer-description';
          footer.append(title, detail); container.append(footer);
        }
        text(footer.querySelector('.gz-v4-footer-title'), heading);
        footer.querySelector('strong').hidden = !heading;
        if (!heading) footer.dataset.gzShippingOnly = 'true';
        else delete footer.dataset.gzShippingOnly;
        const detail = footer.querySelector('.gz-v4-footer-description');
        const detailSignature = JSON.stringify([description, safe ? links.map(link => link ? ['href','target','rel'].map(attr => link.getAttribute(attr)) : null) : []]);
        if (descriptions.get(detail) !== detailSignature) {
          const nodes = [];
          if (names.length) {
            names.forEach((name, index) => {
              if (index) nodes.push(document.createTextNode(' + '));
              if (links[index]) {
                const anchor = document.createElement('a');
                anchor.className = 'gz-v4-gift-link'; anchor.textContent = name;
                for (const attribute of ['href','target','rel']) {
                  const value = links[index].getAttribute(attribute);
                  if (value !== null) anchor.setAttribute(attribute, value);
                }
                nodes.push(anchor);
              } else nodes.push(document.createTextNode(name));
            });
            if (guide) nodes.push(document.createTextNode(' + guía'));
          } else nodes.push(document.createTextNode(description));
          detail.replaceChildren(...nodes); descriptions.set(detail, detailSignature);
        }
        if (deal.quantity === 1) {
          const badge = bar.querySelector('.kaching-bundles__bar-label');
          if (badge) { if (!badges.has(badge)) badges.set(badge, badge.textContent); text(badge, 'Para empezar'); }
        }
        const image = bar.querySelector('.kaching-bundles__bar-image');
        const source = deal.image||policy.packImages?.[String(deal.quantity)];
        if (image && source && image.getAttribute('src') !== source) {
          const previous = images.get(image);
          images.set(image, {original:previous && image.getAttribute('src') === previous.applied ? previous.original : image.getAttribute('src'), applied:source});
          image.setAttribute('src', source);
        }
      }
    }
  };
  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => { queued = false; update(); });
  });
  observer.observe(form, {childList:true, subtree:true, characterData:true, attributes:true,
    attributeFilter:['deal-block','data-deal-block-id','data-ab-test-variant-id','data-deal-bar-id','class','role','tabindex','href','target','rel']});
  update();
})();
